import React, { useState, useEffect, useContext } from "react";
import NumberFormat from "react-number-format";
import { formatCurrency, formatPhoneNumber, getPlanDetails, validateEmail } from "../../../utils";
import { Phone_Fax_Regex, colors } from "../../../utils/constants";
import Button from "../../Button/Button";
import Checkbox from "../../Checkbox/Checkbox";
import DatePicker from "../../DatePicker/DatePicker";
import TextInput from "../../TextInput/TextInput";
import { ToastContext } from "../../../contexts/ToastContext/ToastContext";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/store";
import { useParams } from "react-router-dom";
import moment from "moment";
import { updateOfficeUser } from "../../../redux/slices/UserSlice";
import { User } from "../../../models/user.model";
import { UserUpdate } from "../../../models/user-update.model";
import { UserAnnualPlanState } from "../../../models/user-annual-plan.model";
import { calculateAge, isValidDate } from "../../../utils/date";
import { getOfficeCareProviders } from "../../../redux/slices/OfficeSlice";
import { AuthPermission } from "../../../services/auth.service";
import { ToastVariants } from "../../../models";

interface formDataType {
  id: string;
  value: string | boolean;
  title: string;
  placeholder: string;
  required: boolean;
  errorMessage: string;
  valid: boolean;
  type: "textInput" | "ohip" | "birthday" | "checkbox" | "billingPlan" | "textArea" | "primaryCareProviderId";
}

const formDataInitialState: formDataType[] = [
  {
    id: "firstName",
    value: "",
    title: "First Name",
    placeholder: "First Name",
    required: true,
    errorMessage: "Please provide first name",
    valid: false,
    type: "textInput",
  },
  {
    id: "lastName",
    value: "",
    title: "Last Name",
    placeholder: "Last Name",
    required: true,
    errorMessage: "Please provide last name",
    valid: false,
    type: "textInput",
  },
  {
    id: "email",
    value: "",
    title: "Email",
    placeholder: "Email Address",
    required: true,
    errorMessage: "Please provide valid email address",
    valid: false,
    type: "textInput",
  },
  {
    id: "dateOfBirth",
    value: "",
    title: "Date of Birth (DD/MM/YYYY)",
    placeholder: "DD/MM/YYYY",
    required: true,
    errorMessage: "Please enter valid date of birth",
    valid: false,
    type: "birthday",
  },
  {
    id: "phone",
    value: "",
    title: "Phone",
    placeholder: "Enter Phone number",
    required: true,
    errorMessage: "Please enter valid phone number",
    valid: false,
    type: "textInput",
  },
  {
    id: "ohipNumber",
    value: "",
    title: "Health Card Number",
    placeholder: "Health Card #",
    required: true,
    errorMessage: "Please enter Health Card Number",
    valid: false,
    type: "ohip",
  },
  {
    id: "primaryCareProviderId",
    value: "",
    title: "Associated PCP",
    placeholder: "",
    required: false,
    errorMessage: "",
    valid: false,
    type: "primaryCareProviderId",
  },
  {
    id: "billingPlan",
    value: "",
    title: "Active Billing Plan",
    placeholder: "",
    required: false,
    errorMessage: "Please select billing plan",
    valid: false,
    type: "billingPlan",
  },
  {
    id: "annualPlanMember",
    value: "",
    title: "Included in family or couples plan",
    placeholder: "",
    required: false,
    errorMessage: "",
    valid: false,
    type: "checkbox",
  },
  {
    id: "userAnnualPlanDate",
    value: "",
    title: "Annual Plan Renewal Date (DD/MM/YYYY)",
    placeholder: "DD/MM/YYYY",
    required: false,
    errorMessage: "Please select date",
    valid: false,
    type: "birthday",
  },
  {
    id: "note",
    value: "",
    title: "Notes",
    placeholder: "",
    required: false,
    errorMessage: "Please add notes",
    valid: false,
    type: "textInput",
  },
  {
    id: "verified",
    value: "",
    title: "This patient has been verified.",
    placeholder: "",
    required: false,
    errorMessage: "",
    valid: false,
    type: "checkbox",
  },
];

const EditPatientProfile = ({
  onClickCancel,
  isEditable = false,
  userData,
  setIsLoading,
  isLoading,
}: {
  onClickCancel: (val?: boolean) => void;
  isEditable?: boolean;
  userData?: User;
  setIsLoading?: (val: boolean) => void;
  isLoading?: boolean;
}) => {
  const toast = useContext(ToastContext);
  const dispatch = useDispatch<AppDispatch>();
  const { id } = useParams();
  const [formData, setFormData] = useState(formDataInitialState);
  const [errorInitiated, setErrorInitiated] = useState(false);
  const { verificationError } = useSelector((state: RootState) => state.patient);
  const planDetails = getPlanDetails(userData?.userAnnualPlan?.officeAnnualPlan);
  const { primaryCareProvider } = useSelector((state: RootState) => state.office);

  useEffect(() => {
    if (id) {
      const officeId = id;
      dispatch(getOfficeCareProviders({ officeId }));
    }
  }, [id]);

  const [careProvider, setCareProvider] = useState<string>("");

  const handleCareProvider = (userId: string) => {
    // Empty value = "Select Care Provider"
    const selectedId = userId || "";

    setCareProvider(selectedId);

    // Keep formData in sync so dropdown shows selected value and payload has the correct id
    setFormData(prev =>
      prev.map(input =>
        input.id === "primaryCareProviderId"
          ? {
              ...input,
              value: selectedId,
              valid: true,
            }
          : input
      )
    );
  };

  const handleChange = (id: string, value: string | boolean) => {
    let tempFormData = [...formData];
    tempFormData = tempFormData.map(input => {
      const newInput = input;
      if (input.id === id) {
        if (id === "phone") {
          newInput.value = formatPhoneNumber(value as string);
        } else if (id === "verified" || id === "annualPlanMember") {
          newInput.value = typeof value === "boolean" && value ? "yes" : "no";
        } else {
          newInput.value = value;
        }
        newInput.valid = validateFormField(input.id, value as string, input.required);
        // Set specific error message for DOB age validation
        if (id === "dateOfBirth") {
          const dobVal = value as string;
          if (dobVal && isValidDate(dobVal)) {
            const age = calculateAge(dobVal);
            if (!Number.isNaN(age) && age < 18) {
              newInput.errorMessage = "You must be at least 18 years old";
            } else {
              newInput.errorMessage = "Please enter valid date of birth";
            }
          }
        }
      }
      return newInput;
    });

    // If annualPlanMember toggles, update userAnnualPlanDate field visibility/requirement
    if (id === "annualPlanMember") {
      const isMember = typeof value === "boolean" ? value : value === "yes";
      tempFormData = tempFormData.map(input => {
        if (input.id === "userAnnualPlanDate") {
          const updated = { ...input };
          updated.required = isMember;
          if (!isMember) {
            updated.value = "";
            updated.valid = true; // not required when hidden
          } else {
            updated.valid = validateFormField(updated.id, updated.value as string, true);
          }
          return updated;
        }
        return input;
      });
    }

    setFormData(tempFormData);
  };

  const validateForm = () => {
    // If annualPlanMember is checked, enforce userAnnualPlanDate required
    const isMember = (formData.find(i => i.id === "annualPlanMember")?.value as string) === "yes";
    return formData.every(val => {
      if (val.id === "userAnnualPlanDate") {
        return isMember ? val.valid : true;
      }
      return !val.required || (val.required && val.valid);
    });
  };

  const handleClickSave = async () => {
    if (!isEditable) {
      toast?.openToast();
      return;
    }
    if (!validateForm()) {
      setErrorInitiated(true);
      return;
    }
    const firstName = (formData.find(item => item.id === "firstName")?.value as string).trim();
    const lastName = (formData.find(item => item.id === "lastName")?.value as string).trim();
    const email = (formData.find(item => item.id === "email")?.value as string).trim();
    const phone = (formData.find(item => item.id === "phone")?.value as string).trim();
    const dateOfBirth = (formData.find(item => item.id === "dateOfBirth")?.value as string).trim();
    const ohipNumber = (formData.find(item => item.id === "ohipNumber")?.value as string).trim();
    const billingPlan = (formData.find(item => item.id === "billingPlan")?.value as string).trim();
    const annualPlanMember = (formData.find(item => item.id === "annualPlanMember")?.value as string).trim();
    const verified = (formData.find(item => item.id === "verified")?.value as string).trim();
    const userAnnualPlanDate = (formData.find(item => item.id === "userAnnualPlanDate")?.value as string).trim();
    const primaryCareProviderId = careProvider;

    const note = (formData.find(item => item.id === "note")?.value as string).trim();
    if (id && userData?.userId && email && firstName && lastName) {
      const updatedUser: UserUpdate = {
        userId: userData?.userId,
        officeId: id,
        firstName,
        lastName,
        email,
        phone,
        dateOfBirth,
        ohipNumber,
        note,
        annualPlanMember: annualPlanMember === "yes" ? true : false,
        verified: verified === "yes" ? true : false,
        primaryCareProviderId,
      };

      if (annualPlanMember === "yes" && userAnnualPlanDate) {
        (updatedUser as any).userAnnualPlanDate = userAnnualPlanDate;
      }

      setIsLoading ? setIsLoading(true) : null;
      const resp = await dispatch(
        updateOfficeUser({
          officeId: id,
          user: updatedUser,
        })
      );
      setIsLoading ? setIsLoading(false) : null;
      const payload = resp.payload as any;
      if (resp?.payload) {
        if (payload?.message) {
          toast?.openToast(payload?.message, 2000, ToastVariants.Error);
        } else {
          toast?.openToast("Patient profile updated successfully!");
        }
        onClickCancel(true);
      } else {
        toast?.openToast("Patient profile update failed");
      }
    }
  };

  const renderInput = (inp: formDataType) => {
    switch (inp.type) {
      case "textInput":
        return (
          <TextInput
            key={inp.id}
            value={inp.value as string}
            onChangeFunc={e => handleChange(inp.id, e.currentTarget.value)}
            placeholder={inp.placeholder}
            errorMsg={inp.errorMessage}
            isError={!inp.valid && errorInitiated}
            label={inp.title}
            isRequired={inp.required}
            readonly={!isEditable}
            onBlur={e => handleChange(inp.id, e.currentTarget.value.trim())}
            isDarkBg={true}
            isTextArea={inp.id === "note"}
            extraInputClass={inp.id === "note" ? "resize-none h-40" : ""}
          />
        );
      case "ohip":
        return (
          <div key={inp.id}>
            <label className="text-sm text-docsigna-dark-blue font-medium block w-full mb-0.5 mt-0 pt-0">
              {inp.title}
              {inp.required ? <span className="text-red-500">*</span> : null}
            </label>
            {/* Hidden honeypot field to confuse browser autofill */}
            <input type="text" name="cc-number" style={{ display: "none" }} tabIndex={-1} autoComplete="off" />
            <NumberFormat
              value={inp.value as string}
              isNumericString
              format={"#### ### ###"}
              placeholder="Health Card #"
              displayType="input"
              inputMode="numeric"
              name={`hc_${Date.now()}`}
              id="provincial-health-id"
              autoComplete="one-time-code"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
              aria-autocomplete="none"
              readOnly={!isEditable}
              className="text-base block font-medium w-full rounded-md bg-docsigna-pink-light border-docsigna-blue-dark"
              onValueChange={values => handleChange(inp.id, values.value)}
            />
            {!inp.valid && errorInitiated ? <p style={{ color: colors.Red }}>{inp.errorMessage}</p> : null}
          </div>
        );
      case "primaryCareProviderId":
        return (
          <div key={inp.id}>
            <label className="text-sm text-docsigna-dark-blue font-medium block w-full mb-0.5 mt-0 pt-0">
              {inp.title}
              {inp.required ? <span className="text-red-500">*</span> : null}
            </label>{" "}
            <div className="w-full">
              <select
                disabled={!isEditable}
                onChange={e => handleCareProvider(e.currentTarget.value)}
                value={(inp.value as string) || ""}
                className="general-select w-full bg-transparent"
              >
                <option value="">Select Care Provider</option>
                {primaryCareProvider
                  ?.filter(care => care.userState === "active")
                  .map(care => (
                    <option value={care.userId} key={care.userId}>
                      {`${care.firstName} ${care.lastName}`}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        );

      case "birthday":
        // Hide userAnnualPlanDate when not a member
        if (inp.id === "userAnnualPlanDate") {
          const isMember = (formData.find(i => i.id === "annualPlanMember")?.value as string) === "yes";
          if (!isMember) return null;
        }
        return !isEditable ? (
          <TextInput
            key={inp.id}
            value={moment(inp.value as string).format("MMM DD, YYYY")}
            onChangeFunc={e => handleChange(inp.id, e.currentTarget.value)}
            placeholder={inp.placeholder}
            errorMsg={inp.errorMessage}
            isError={!inp.valid && errorInitiated}
            label={inp.title}
            isRequired={inp.required}
            readonly={true}
            isDarkBg={true}
          />
        ) : (
          <DatePicker
            key={inp.id}
            label={inp.title}
            isRequired={inp.required}
            name={inp.id}
            placeholder={inp.placeholder}
            value={inp.value as string}
            isError={!inp.valid && errorInitiated}
            onChange={val => handleChange(inp.id, val)}
            errorMsg={inp.errorMessage}
            disabled={!isEditable}
            isDarkBg={true}
            isDob={inp.id !== "userAnnualPlanDate"}
            preventErase={inp.id === "userAnnualPlanDate"}
            futureOnly={inp.id === "userAnnualPlanDate"}
            dateFormat="DD/MM/YYYY"
          />
        );

      case "checkbox":
        return inp.id === "annualPlanMember" && userData?.userAnnualPlan?.userAnnualPlanState === UserAnnualPlanState.Active ? null : (
          <div className={`${inp.id === "annualPlanMember" ? "-mt-4" : ""}`}>
            <Checkbox
              key={inp.id}
              id={inp.id}
              label={inp.title}
              checked={inp.value === "yes"}
              name={inp.id}
              onChange={e => handleChange(inp.id, e.currentTarget.checked)}
              disabled={!isEditable}
              isDarkBg={true}
            />
            {!inp.valid && errorInitiated ? <p style={{ color: colors.Red }}>{inp.errorMessage}</p> : null}
          </div>
        );
      case "billingPlan":
        return (
          <div key={inp.id}>
            <label className="text-sm text-docsigna-dark-blue font-medium block w-full mb-0.5 mt-0 pt-0">
              {inp.title}
              {inp.required ? <span className="text-red-500">*</span> : null}
            </label>{" "}
            <div className="w-full">
              <select
                disabled={!isEditable}
                onChange={e => handleChange(inp.id, e.currentTarget.value)}
                value={inp.value as string}
                className="general-select w-full bg-transparent"
              >
                {userData?.userAnnualPlan ? (
                  <option value="">
                    {planDetails?.planName ?? ""} {planDetails?.price}/{planDetails?.subscriptionType}
                  </option>
                ) : (
                  <option value="">No active plan</option>
                )}
              </select>
              <div className="mb-2">{!inp.valid && errorInitiated ? <p style={{ color: colors.Red }}>{inp.errorMessage}</p> : null}</div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const validateFormField = (name: string, value: string | any, required: boolean) => {
    if (!required && !value) return true;

    let valid = false;

    switch (name) {
      case "phone":
        valid = Phone_Fax_Regex.test(formatPhoneNumber(value as string));
        break;
      case "email":
        valid = validateEmail(value as string);
        break;
      case "ohip":
        valid = (value as string).trim() !== "" ? (value as string).length === 10 : true;
        break;
      case "dateOfBirth":
        valid =
          typeof value === "string" && value.length > 0 && isValidDate(value) && !Number.isNaN(calculateAge(value)) && calculateAge(value) >= 18;
        break;
      default:
        valid = typeof value === "string" ? value.length > 0 : true;
        break;
    }

    return valid;
  };

  const populatePatientFormData = (user?: User): formDataType[] => {
    const newFormData = formDataInitialState;
    if (!user) return newFormData;
    const newData = newFormData.map(item => {
      const newItem = { ...item };
      switch (newItem.id) {
        case "verified":
          newItem.value = user.verified ? "yes" : "no";
          break;
        case "annualPlanMember":
          newItem.value = user?.annualPlanMember ? "yes" : "no";
          break;
        default:
          newItem.value = user[item.id as keyof User]?.toString() ?? "";
          newItem.valid = validateFormField(newItem.id, newItem.value, newItem.required);
          break;
      }
      return newItem;
    });

    // Pre-select PCP from either primaryCareProviderId or primaryCareProviderInfo (backend response)
    const primaryCareProviderIdFromUser = (user as any).primaryCareProviderId as string | undefined;
    const primaryCareProviderInfo = (user as any).primaryCareProviderInfo as { userId?: string } | undefined;
    const resolvedPcpId = primaryCareProviderIdFromUser || primaryCareProviderInfo?.userId || "";

    if (resolvedPcpId) {
      return newData.map(input => (input.id === "primaryCareProviderId" ? { ...input, value: resolvedPcpId, valid: true } : input));
    }

    // If user is already an annual plan member initially, enforce userAnnualPlanDate as required and validate it
    if (user?.annualPlanMember) {
      const updatedData = newData.map(input => {
        if (input.id === "userAnnualPlanDate") {
          const updated = { ...input };
          updated.required = true;
          updated.valid = validateFormField(updated.id, updated.value as string, true);
          return updated;
        }
        return input;
      });
      return updatedData;
    }

    return newData;
  };

  useEffect(() => {
    if (userData) {
      const newData = populatePatientFormData(userData);
      setFormData(newData);

      // Keep local PCP state in sync so payload uses the same id
      const primaryCareProviderIdFromUser = (userData as any).primaryCareProviderId as string | undefined;
      const primaryCareProviderInfo = (userData as any).primaryCareProviderInfo as { userId?: string } | undefined;
      const resolvedPrimaryCareProviderId = primaryCareProviderIdFromUser || primaryCareProviderInfo?.userId || "";
      if (resolvedPrimaryCareProviderId) {
        setCareProvider(resolvedPrimaryCareProviderId);
      }
    }
  }, [userData]);

  useEffect(() => {
    return () => {
      setFormData(formDataInitialState);
    };
  }, [dispatch, id]);

  return (
    <>
      {verificationError ? <p className="text-sm px-8 py-5 text-red-400 text-center">An error has occurred, please try again</p> : null}
      {formData.map(input =>
        input.id === "patientFirstName" || input.id === "patientLastName" ? (
          <div key={input.id} className="px-8 py-2">
            {renderInput(input)}
          </div>
        ) : null
      )}

      {formData.map(input =>
        input.id === "patientFirstName" || input.id === "patientLastName" ? null : input.id === "patientVerified" ? (
          userData?.userType !== "patient" ? (
            <div key={input.id} className="px-8 py-2">
              {renderInput(input)}
            </div>
          ) : null
        ) : (
          <div key={input.id} className="px-8 py-2">
            {renderInput(input)}
          </div>
        )
      )}

      <div className="fixed bottom-0 right-0 w-11/12 max-w-120 z-30 bg-docsigna-pink-light">
        <div className="px-8 py-5 flex justify-end">
          <Button
            disabled={isLoading}
            onClick={() => onClickCancel()}
            className="pointer px-5 mr-3"
            text={isEditable ? "Cancel" : "Close"}
            width="fit"
            variant={isEditable ? "Secondary" : "Primary"}
          />
          {isEditable ? (
            <div>
              <Button
                onClick={() => {
                  handleClickSave();
                }}
                disabled={isLoading || (!validateForm() && errorInitiated)}
                className="pointer px-5"
                text="Save"
                width="fit"
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default EditPatientProfile;
